import React, { useState } from 'react';
import { Platform } from '../../../types/Platforms';
import { PlatformDescription, getPlatformLabel, getPlatformColor, getPlatformIcon } from '../types';

interface PlatformDescriptionCardProps {
  description: PlatformDescription;
  onUpdate: (updates: Partial<PlatformDescription>) => void;
}

export const PlatformDescriptionCard: React.FC<PlatformDescriptionCardProps> = ({
  description,
  onUpdate,
}) => {
  const [newHashtag, setNewHashtag] = useState('');
  const showTitle = description.platform === Platform.YOUTUBE_SHORTS;

  const handleAddHashtag = () => {
    const tag = newHashtag.trim().replace(/^#/, '');
    if (tag) {
      onUpdate({ hashtags: [...description.hashtags, `#${tag}`] });
      setNewHashtag('');
    }
  };

  const handleRemoveHashtag = (index: number) => {
    onUpdate({
      hashtags: description.hashtags.filter((_, i) => i !== index),
    });
  };

  const handleHashtagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddHashtag();
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Platform header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50">
        <div
          className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs ${getPlatformColor(
            description.platform
          )}`}
        >
          <span className="material-icons text-xs">
            {getPlatformIcon(description.platform)}
          </span>
        </div>
        <span className="text-sm font-semibold text-[#2f4941]">
          {getPlatformLabel(description.platform)}
        </span>
      </div>

      <div className="p-4 space-y-4">
        {/* Title field (YouTube only) */}
        {showTitle && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Title
            </label>
            <input
              type="text"
              value={description.title}
              onChange={(e) => onUpdate({ title: e.target.value })}
              placeholder="Video title..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f4941] focus:border-[#2f4941]"
            />
          </div>
        )}

        {/* Content / Caption */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {showTitle ? 'Description' : 'Caption'}
          </label>
          <textarea
            value={description.content}
            onChange={(e) => onUpdate({ content: e.target.value })}
            placeholder={showTitle ? 'Video description...' : 'Write your caption...'}
            rows={4}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#2f4941] focus:border-[#2f4941]"
          />
          <p className="text-xs text-gray-400 mt-1">
            {description.content.length} characters
          </p>
        </div>

        {/* Hashtags */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Hashtags
          </label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {description.hashtags.map((tag, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 text-xs text-[#2f4941] bg-[#2f4941]/10 px-2 py-1 rounded-full"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => handleRemoveHashtag(idx)}
                  className="hover:text-red-500 transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newHashtag}
              onChange={(e) => setNewHashtag(e.target.value)}
              onKeyDown={handleHashtagKeyDown}
              placeholder="Add hashtag..."
              className="flex-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f4941] focus:border-[#2f4941]"
            />
            <button
              type="button"
              onClick={handleAddHashtag}
              disabled={!newHashtag.trim()}
              className="px-3 py-1.5 text-xs bg-[#2f4941] text-white rounded-lg hover:bg-[#1e3128] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Add
            </button>
          </div>
        </div>

        {/* Call to Action */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Call to Action
          </label>
          <input
            type="text"
            value={description.callToAction}
            onChange={(e) => onUpdate({ callToAction: e.target.value })}
            placeholder="e.g., Visit our website, Follow for more..."
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f4941] focus:border-[#2f4941]"
          />
        </div>
      </div>
    </div>
  );
};

export default PlatformDescriptionCard;
